import { HoverCard, HoverCardContent, HoverCardTrigger } from '@crowdex/ui/components/hover-card';
import { useState } from 'react';
import type { MessageChangedFile } from '@/lib/conversations/message-changes';
import { basename } from '@/lib/desktop-paths';
import { parseReviewDiff } from '@/lib/review-diff';

const MAX_PREVIEW_LINES = 10;

export function FileDiffHover({
  file,
  loadDiff,
  onOpen,
}: {
  file: MessageChangedFile;
  /** The file's diff, fetched from the workspace server the first time the preview opens. */
  loadDiff?: () => Promise<string>;
  onOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [patch, setPatch] = useState<string | null>(null);
  const diff = patch === null ? undefined : parseReviewDiff(patch)[0];
  const lines = diff?.lines.filter((line) => line.kind !== 'meta' && line.kind !== 'hunk') ?? [];
  const previewLines = lines.slice(0, MAX_PREVIEW_LINES);
  const hiddenLineCount = lines.length - previewLines.length;
  const path = file.path;

  function changeOpen(next: boolean) {
    setOpen(next);
    if (next && patch === null && loadDiff !== undefined) {
      void loadDiff().then(setPatch, () => setPatch(''));
    }
  }

  return (
    <HoverCard closeDelay={100} openDelay={250} open={open} onOpenChange={changeOpen}>
      <HoverCardTrigger asChild>
        <button
          className="flex min-h-8 w-full items-center gap-3 rounded-md px-1 text-left hover:bg-control/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          type="button"
          onClick={() => (onOpen ? onOpen() : changeOpen(!open))}
          title={path}
        >
          {/* The file's name reads at a glance; its path is in the title and the hover card. */}
          <span className="min-w-0 flex-1 truncate font-mono text-foreground/85 text-xs">
            {basename(path)}
          </span>
          <span className="shrink-0 font-mono text-xs tabular-nums">
            {file.additions > 0 && <span className="text-success">+{file.additions}</span>}
            {file.additions > 0 && file.deletions > 0 && ' '}
            {file.deletions > 0 && <span className="text-destructive">−{file.deletions}</span>}
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-[min(38rem,calc(100vw-2rem))] overflow-hidden rounded-xl p-0"
        side="top"
        sideOffset={8}
      >
        <div className="flex items-center gap-3 border-border border-b bg-muted/35 px-4 py-3">
          <p className="min-w-0 flex-1 truncate font-medium text-sm">{path}</p>
          <span className="shrink-0 text-sm tabular-nums">
            <span className="text-desktop-success">+{file.additions}</span>{' '}
            <span className="text-destructive">-{file.deletions}</span>
          </span>
        </div>
        <div className="overflow-hidden bg-background font-mono text-xs">
          {previewLines.map((line, index) => (
            <div
              className={`grid min-w-0 grid-cols-[2.75rem_2.75rem_minmax(0,1fr)] leading-5 ${
                line.kind === 'addition'
                  ? 'bg-emerald-500/15'
                  : line.kind === 'deletion'
                    ? 'bg-red-500/15'
                    : line.kind === 'collapsed'
                      ? 'bg-muted/45 text-muted-foreground'
                      : ''
              }`}
              // biome-ignore lint/suspicious/noArrayIndexKey: An excerpt's lines are positions in it (collapsed lines have no number).
              key={`${line.kind}-${line.oldLine ?? ''}-${line.newLine ?? ''}-${index}`}
            >
              <span className="select-none border-hairline-faint border-r px-2 text-right text-muted-foreground/70 tabular-nums">
                {line.oldLine ?? ''}
              </span>
              <span className="select-none border-hairline-faint border-r px-2 text-right text-muted-foreground/70 tabular-nums">
                {line.newLine ?? ''}
              </span>
              <code className="truncate whitespace-pre px-2">
                <span
                  className={
                    line.kind === 'addition'
                      ? 'text-emerald-500'
                      : line.kind === 'deletion'
                        ? 'text-red-500'
                        : 'text-muted-foreground'
                  }
                >
                  {line.kind === 'addition' ? '+' : line.kind === 'deletion' ? '-' : ' '}
                </span>
                {line.content || ' '}
              </code>
            </div>
          ))}
          {hiddenLineCount > 0 && (
            <div className="border-border border-t bg-muted/30 px-3 py-2 text-muted-foreground">
              {hiddenLineCount} more {hiddenLineCount === 1 ? 'line' : 'lines'} — open Review for
              the full diff
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
